"use client";

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from "react";

type FinancialWorkSearchValue = {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
};

const FinancialWorkSearchContext =
  createContext<FinancialWorkSearchValue | null>(null);

export function FinancialWorkSearchProvider({
  searchQuery,
  setSearchQuery,
  children,
}: FinancialWorkSearchValue & { children: ReactNode }) {
  return (
    <FinancialWorkSearchContext.Provider value={{ searchQuery, setSearchQuery }}>
      {children}
    </FinancialWorkSearchContext.Provider>
  );
}

export function useFinancialWorkSearch() {
  return useContext(FinancialWorkSearchContext);
}

/** Keep a table's `filters.q` aligned with the Financial Work header search. */
export function useBindFinancialWorkSearch(
  q: string | null | undefined,
  setFilter: (key: string, value: string | null) => void,
) {
  const ctx = useFinancialWorkSearch();
  const currentQ = (q ?? "").trim();
  const headerQ = (ctx?.searchQuery ?? "").trim();
  const setFilterRef = useRef(setFilter);
  setFilterRef.current = setFilter;

  useEffect(() => {
    if (!ctx) return;
    const next = headerQ || null;
    if (currentQ === (next ?? "")) return;
    setFilterRef.current("q", next);
  }, [ctx, headerQ, currentQ]);
}
